import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import Decimal from 'decimal.js';
import React, {useState} from 'react';

import FieldActionButtons from 'metadata/FieldActionButtons';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {CreateUnitParams, UpdateUnitParams} from './UnitDictionaryDAO';

type Unit = UnitTypesQuery['unitTypes'][number]['units'][number];
type BaseUnit = UnitTypesQuery['unitTypes'][number]['baseUnit'];

const MAX_TEXT_FIELD_LENGTH = 255;

export interface UnitTableRowForEditingOrCreationProps {
  baseUnit: BaseUnit;
  unit?: Unit;
  onSave: (params: CreateUnitParams | UpdateUnitParams) => void;
  onCancel: () => void;
  unitTypeId: string;
}

export default function UnitTableRowForEditingOrCreation({
  unit,
  baseUnit,
  onSave,
  onCancel,
  unitTypeId,
}: UnitTableRowForEditingOrCreationProps): JSX.Element {
  const forCreation = unit == null;
  const isBaseUnit = unit?.id === baseUnit.id;

  const baseUnitSymbol = baseUnit.symbol;
  const [name, setName] = useState(unit?.name ?? '');
  const [conversionFactor, setConversionFactor] = useState(unit?.conversionFactor ?? '');
  const [symbol, setSymbol] = useState(unit?.symbol ?? '');
  const parsedConversionFactor =
    conversionFactor && parseNumber(conversionFactor, {scientificNotationAllowed: true});
  const canSubmit =
    name.trim().length > 0 &&
    symbol.trim().length > 0 &&
    parsedConversionFactor &&
    parsedConversionFactor > 0 &&
    (forCreation || name.trim() !== unit?.name || symbol.trim() !== unit?.symbol);

  const submit = (): void => {
    if (canSubmit) {
      if (forCreation) {
        onSave({
          name: name.trim(),
          symbol: symbol.trim(),
          conversionFactor,
          unitTypeId,
        });
      } else {
        onSave({
          id: unit.id,
          name: name.trim(),
          symbol: symbol.trim(),
        });
      }
    }
  };

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      submit();
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className={css(styles.rowContainer)}
      {...getTestProps({component: 'UnitTableRowForEditingOrCreation', element: 'root'})}
    >
      <div className={css(styles.horizontalSpacing)} />
      <div className={classNames('simpleDivTable-row', 'is-beingEdited')}>
        <div className="simpleDivTable-cell--narrow">
          <div className="simpleDivTable-cell-contents">
            <FormInput
              type="text"
              name="Name"
              value={name}
              onChange={(e: InputChangeEvent) => {
                setName(e.target.value);
              }}
              placeholder="Unit name"
              onKeyDown={handleKeyDown}
              maxLength={MAX_TEXT_FIELD_LENGTH}
              dataTestAttrs={{component: 'UnitTableRowForEditingOrCreation', element: 'nameField'}}
            />
          </div>
        </div>

        <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
          <div className="simpleDivTable-cell-contents">
            <FormInput
              type="text"
              name="Symbol"
              value={symbol}
              onChange={(e: InputChangeEvent) => {
                setSymbol(e.target.value);
              }}
              placeholder="Symbol"
              onKeyDown={handleKeyDown}
              maxLength={MAX_TEXT_FIELD_LENGTH}
              dataTestAttrs={{component: 'UnitTableRowForEditingOrCreation', element: 'symbolField'}}
            />
          </div>
        </div>

        <div className="simpleDivTable-cell">
          {forCreation ? (
            <div className={css(styles.conversionFactorInput)}>
              <FormInput
                className={css(styles.conversionFactorInputField)}
                type="text"
                name="Conversion factor"
                value={conversionFactor}
                onChange={(e: InputChangeEvent) => {
                  setConversionFactor(e.target.value);
                }}
                placeholder="value"
                onKeyDown={handleKeyDown}
                dataTestAttrs={{
                  component: 'UnitTableRowForEditingOrCreation',
                  element: 'conversionFactorField',
                }}
              />
              <span className={css(styles.conversionFactorInputFlex1)}>x</span>
              <span className={css(styles.conversionFactorInputFlex1)}>{baseUnitSymbol}</span>
              <span className={css(styles.conversionFactorInputFlex1)}>=</span>
              <span className={css(styles.conversionFactorInputFlex1)}>1</span>
              <Chip
                className={css(styles.conversionFactorInputFlex1)}
                size={ChipSize.MINI}
                text={symbol || 'Symbol'}
              />
            </div>
          ) : (
            <div className="simpleDivTable-cell-contents--last">
              {isBaseUnit ? (
                <span className={css(styles.baseUnitText)}>Base unit</span>
              ) : (
                <span>{`${new Decimal(unit.conversionFactor).toString()} x ${baseUnitSymbol}`}</span>
              )}
            </div>
          )}
        </div>
        <div className="simpleDivTable-actionGroupContainer">
          <FieldActionButtons
            isEditing={true}
            cancel={onCancel}
            complete={submit}
            isCompleteEnabled={Boolean(canSubmit)}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  archivedField: {
    opacity: 0.6,
  },
  baseUnitText: {
    color: GRAY_DARKER,
    fontStyle: 'italic',
  },
  iconStyle: {
    color: GRAY_DARKER,
    marginRight: '10px',
    height: '12px',
    width: '12px',
  },
  horizontalSpacing: {
    width: 25,
  },
  rowContainer: {
    display: 'flex',
    marginLeft: -25,
    width: '100%',
    position: 'relative',
  },
  tableRow: {
    alignItems: 'center',
    borderTop: 1,
    borderColor: GRAY_LIGHT,
    display: 'flex',
    marginRight: -25,
    padding: '10px 14px',
    width: '100%',
  },
  conversionFactorInput: {
    paddingRight: 82,
    display: 'flex',
    alignItems: 'center',
  },
  conversionFactorInputField: {
    flex: 3,
  },
  conversionFactorInputFlex1: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
});
